// Google Business Profile integration — public description of the reviews
// management feature. Required by Google's OAuth app verification: the
// homepage domain must describe the integration, including the review-reply
// capability, and link to the privacy policy. Linked from the footer
// (Product group). Copy lives in locales under `gbp.*`.

import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../components/icons.jsx";
import { useLocaleNav, useCurrentLocale } from "../lib/useLocaleNav.js";
import { goToBooking, goToLogin } from "../lib/handoff.js";

export default function GoogleBusinessProfile() {
  const { t } = useTranslation();
  const go = useLocaleNav();
  const locale = useCurrentLocale();
  const steps = t("gbp.steps", { returnObjects: true });
  const accessList = t("gbp.accessList", { returnObjects: true });
  const neverList = t("gbp.neverList", { returnObjects: true });
  const dataList = t("gbp.dataList", { returnObjects: true });

  return (
    <main>
      {/* HERO */}
      <section className="bg-wash">
        <div className="container" style={{ padding: "56px 24px 96px" }}>
          <AmberEyebrow label={t("gbp.eyebrow")} />
          <h1 className="tt-h tt-h-xl" style={{ marginTop: 24, maxWidth: 1100 }}>
            {t("gbp.headline")}<br />
            <span style={{ fontFamily: "Fraunces, serif", fontStyle: "italic", fontWeight: 400 }}>{t("gbp.headlineItalic")}</span>
          </h1>
          <p style={{ marginTop: 40, maxWidth: 820, fontSize: "clamp(18px, 1.4vw, 22px)", lineHeight: 1.6, color: "var(--ink-2)", fontFamily: "Inter, sans-serif" }}>
            {t("gbp.intro")}
          </p>
        </div>
      </section>

      {/* HOW IT WORKS — three beats */}
      <section style={{ background: "var(--bg)", padding: "120px 0", borderBottom: "1px solid var(--line)" }}>
        <div className="container">
          <AmberEyebrow label={t("gbp.stepsEyebrow")} />
          <div style={{ marginTop: 56, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0, borderTop: "1px solid var(--ink)" }} className="gbp-grid">
            {steps.map((s, i) => (
              <div key={i} style={{ padding: "40px 28px 40px 0", borderRight: i < 2 ? "1px solid var(--line)" : 0, paddingLeft: i > 0 ? 28 : 0 }} className="gbp-cell">
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".22em", textTransform: "uppercase", color: "var(--persimmon)" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h2 style={{ margin: "20px 0 0", fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700, fontSize: "clamp(24px, 2.2vw, 32px)", letterSpacing: "-.02em", lineHeight: 1.15, color: "var(--ink)" }}>{s.h}</h2>
                <p style={{ margin: "16px 0 0", fontSize: 16.5, lineHeight: 1.65, color: "var(--ink-2)", maxWidth: 380 }}>{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE ACCESS / NEVER DO */}
      <section style={{ background: "var(--bg-2)", padding: "120px 0" }}>
        <div className="container">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 64 }} className="gbp-two">
            <div>
              <AmberEyebrow label={t("gbp.accessEyebrow")} />
              <p style={prose}>{t("gbp.accessIntro")}</p>
              <ol style={listStyle}>
                {accessList.map((li, i) => <li key={i}>{li}</li>)}
              </ol>
            </div>
            <div>
              <AmberEyebrow label={t("gbp.neverEyebrow")} />
              <ul style={{ ...listStyle, marginTop: 28, listStyle: "none", paddingLeft: 0 }}>
                {neverList.map((li, i) => (
                  <li key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <span style={{ flexShrink: 0, marginTop: 9, width: 8, height: 8, borderRadius: "50%", background: "var(--ink)" }} />
                    <span>{li}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AI DRAFTS */}
      <section style={{ background: "var(--ink)", color: "var(--bg)", padding: "140px 0" }}>
        <div className="container">
          <AmberEyebrow label={t("gbp.aiEyebrow")} marigold />
          <h2 style={{ margin: "32px 0 0", fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700, fontSize: "clamp(40px, 5.5vw, 88px)", lineHeight: 0.98, letterSpacing: "-0.04em", color: "var(--bg)", maxWidth: 1100 }}>
            {t("gbp.aiH")}
          </h2>
          <p style={{ marginTop: 40, maxWidth: 820, fontSize: "clamp(17px, 1.3vw, 20px)", lineHeight: 1.6, color: "rgba(255,255,255,.82)", fontFamily: "Inter, sans-serif" }}>{t("gbp.aiP1")}</p>
          <p style={{ marginTop: 20, maxWidth: 820, fontSize: "clamp(17px, 1.3vw, 20px)", lineHeight: 1.6, color: "rgba(255,255,255,.82)", fontFamily: "Inter, sans-serif" }}>{t("gbp.aiP2")}</p>
        </div>
      </section>

      {/* DATA & CONTROL */}
      <section style={{ background: "var(--bg)", padding: "120px 0", borderBottom: "1px solid var(--line)" }}>
        <div className="container">
          <AmberEyebrow label={t("gbp.dataEyebrow")} />
          <ul style={{ ...listStyle, marginTop: 28, listStyle: "none", paddingLeft: 0, maxWidth: 820 }}>
            {dataList.map((li, i) => (
              <li key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <span style={{ flexShrink: 0, marginTop: 9, width: 8, height: 8, borderRadius: "50%", background: "var(--persimmon)" }} />
                <span>{li}</span>
              </li>
            ))}
          </ul>
          <p style={{ ...prose, marginTop: 32 }}>
            <a href={`/${locale}/privacy#google-business-profile`} className="ulink" onClick={(e) => { e.preventDefault(); go("privacy", "google-business-profile"); }}>
              {t("gbp.dataLink")} →
            </a>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-bold" style={{ padding: "140px 0" }}>
        <div className="container">
          <h2 className="tt-h tt-h-xl" style={{ color: "var(--ink)" }}>{t("gbp.ctaH")}</h2>
          <div style={{ marginTop: 40, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <a href="#" className="btn btn-lg" onClick={(e) => { e.preventDefault(); goToBooking(); }} style={{ background: "var(--ink)", color: "var(--bg)" }}>
              {t("common.bookDemo")} <Icon name="arrow-right" size={16} />
            </a>
            <a href="#" className="btn btn-lg btn-ghost" onClick={(e) => { e.preventDefault(); goToLogin(); }}>
              {t("gbp.ctaOpen")}
            </a>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .gbp-grid { grid-template-columns: 1fr !important; }
          .gbp-cell { border-right: 0 !important; border-bottom: 1px solid var(--line); padding-left: 0 !important; }
          .gbp-cell:last-child { border-bottom: 0; }
          .gbp-two { grid-template-columns: 1fr !important; gap: 56px !important; }
        }
      `}</style>
    </main>
  );
}

function AmberEyebrow({ label, marigold = false }) {
  const color = marigold ? "var(--marigold)" : "var(--persimmon)";
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 12, fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".22em", textTransform: "uppercase", color }}>
      <span style={{ width: 22, height: 1, background: color }} />
      {label}
    </div>
  );
}

const prose = {
  marginTop: 24,
  fontSize: 17,
  lineHeight: 1.65,
  color: "var(--ink-2)",
  maxWidth: 560
};

const listStyle = {
  marginTop: 16,
  paddingLeft: 22,
  fontSize: 17,
  lineHeight: 1.65,
  color: "var(--ink)",
  maxWidth: 560,
  display: "flex",
  flexDirection: "column",
  gap: 14
};
